import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { BoxRequest, Statuses as BoxRequestStatuses } from '@/models/BoxRequest';
import StyledBox, { StyledBoxOptions } from '@/support/StyledBoxType';
import ActionViewIcon from '@/components/action-icon/action-view-icon/ActionViewIcon.vue';
import { RoleEnum, User } from '@/models/User';
import LoaderCard from '@/components/loader-card/LoaderCard';
import { AxiosError } from 'axios';
import ErrorHandler from '@/support/ErrorHandler';
import { Options } from '@/components/mi-dialog/MiDialog';
import ActionEditIcon from '@/components/action-icon/action-edit-icon/ActionEditIcon.vue';
import ActionEditCancelIcon from '@/components/action-icon/action-edit-cancel-icon/ActionEditCancelIcon.vue';
import { styles } from '@/support/Style';
import { Avatar } from '@/support/Avatar';
import ActionDeleteIcon from '@/components/action-icon/action-delete-icon/ActionDeleteIcon';
import { DateTime } from 'luxon';
import { LocaleMessage } from 'vue-i18n';

@Component<RequestKanbanBoardBlock>({
  components: {
    'action-edit-icon': ActionEditIcon,
    'action-edit-cancel-icon': ActionEditCancelIcon,
    'action-delete-icon': ActionDeleteIcon,
    'action-view-icon': ActionViewIcon,
    'loader-card': LoaderCard,
  },
  filters: {
    format: (date: string) => {
      if (!date || !DateTime.fromSQL(date).isValid) {
        return '-';
      }
      return DateTime.fromSQL(date).toFormat('dd-LL-yyyy');
    },
  },
})
export default class RequestKanbanBoardBlock extends Vue {
  @Prop()
  protected request!: BoxRequest;

  @Prop()
  protected user!: User;

  @Prop()
  protected stylists!: User[];

  protected status: string = '';

  protected note = '';

  protected selectedStylist: User | null = null;

  protected isLoading: boolean = false;

  protected isEditing = false;

  protected mounted(): void {
    this.note = this.request.note || '';
    if (this.request && this.request.stylist) {
      this.selectedStylist = new User(this.request.stylist);
    }
  }

  protected getFormattedAddress(user: User): string {
    if (!user.profile) { return ''; }
    return `${user.profile.city}, ${user.profile.country}`;
  }

  protected get isAdminOrPlanner(): boolean {
    return this.user.hasRole(RoleEnum.ADMINISTRATOR) || this.user.hasRole(RoleEnum.PLANNER);
  }

  protected getTypeText(type?: string): string | LocaleMessage {
    if (!type) return '-';
    const result = StyledBox.styledBoxOptions.find(option => option.value === type);
    return result && result.text ? result.text : '';
  }

  protected getUserStatusLabel(status?: string): string {
    if (!status) return '-';
    switch (status) {
      case 'active':
        return this.$tc('Active');
      case 'blacklist':
        return this.$tc('Blacklist');
      case 'discontinued':
        return this.$tc('Discontinued');
      case 'open_invoice':
        return this.$tc('Open invoice');
      default:
        return this.$tc('Unknown');
    }
  }

  protected reset(): void {
    this.selectedStylist = null;
  }

  protected handleEditClick(): void {
    this.isEditing = true;
    this.$nextTick((): void => {
      this.$emit('resize');
    });
  }

  protected handleEditCancelClick(): void {
    this.isEditing = false;
    this.$nextTick((): void => {
      this.$emit('resize');
    });
  }

  protected handleReadMoreClick(): void {
    this.$nextTick((): void => {
      this.$emit('resize');
    });
  }

  protected handleStylistClick(): void {
    this.$router.push(`/stylisten/${this.request.stylist ? this.request.stylist.id : ''}`);
  }

  protected handleEvent(event: any): void {
    //
  }

  // protected handleViewClick(): void {
  //   this.user
  //     ? this.$emit('navigate-to-request', this.request.id)
  //     : this.$router.push(`/aanvragen/${this.request.id}`);
  // }

  protected handleNameClick(): void {
    this.$router.push(`/contacten/${this.request.user ? this.request.user.id : ''}`);
  }

  protected async updateRequest(): Promise<void> {
    this.isLoading = true;
    const request: BoxRequest = await this.request
      .include('stylist')
      .update(this.requestUpdatePayload)
      .catch((error: AxiosError): null => {
        ErrorHandler.network(error);
        return null;
      });
    this.$emit('refresh');
    this.isEditing = false;
    this.isLoading = false;
  }

  protected get hasAge(): boolean {
    if (!this.request || !this.request.user || !this.request.user.profile || !this.request.user.profile.age) {
      return false;
    }

    return !! this.request.user.profile.age;
  }

  protected get statusses(): BoxRequestStatuses {
    return BoxRequest.statuses;
  }

  protected get types(): StyledBoxOptions[] {
    return StyledBox.styledBoxOptions;
  }

  protected get hasStylist(): boolean {
    if (!this.request || !this.request.stylist) {
      return false;
    }

    // Either [] or an object, if object
    return !Array.isArray(this.request.stylist);
  }

  protected get type(): string | LocaleMessage {
    const result: StyledBoxOptions | undefined = this.types.find(
      (type: StyledBoxOptions): boolean => type.value === this.request.type,
    );
    return result && result.text ? result.text : '';
  }

  protected get stylistUserAvatar(): string {
    if ((this.request.stylist as any).avatar && !(this.request.stylist as any).avatar.includes('gravatar')) {
      return (this.request.stylist as any).avatar;
    }

    return new Avatar((this.request.stylist as any).name ? (this.request.stylist as any).name : 'Niet Bekend', {
      color: styles['white'],
      background: styles['primary'],
      size: 24,
    }).toDataUrl();
  }

  protected get isLate(): boolean {
    if (!this.request) {
      return false;
    }

    let date = new Date();
    date.setTime(date.getTime() - 24 * 60 * 60 * 1000 * 5);

    // @ts-ignore
    return date > new Date(this.request.updated_at);
  }

  // protected createdAt(): string {
  //   if (! this.request.created_at) { return ''; }
  //   const dateTime = this.request.created_at;
  //   const date = dateTime.split(' ')[0];
  //   const nlDate = date.split('-').reverse().join('-');
  //   return nlDate;
  // }

  // protected get assignedAt(): string {
  //   if (! this.request.assigned_at) { return ''; }
  //   const dateTime = this.request.assigned_at;
  //   const date = dateTime.split(' ')[0];
  //   const dateNL = date.split('-').reverse().join('-');
  //   if (dateNL == '00-00-0000') {
  //     return '';
  //   }

  //   return dateNL;
  // }

  protected get requestUpdatePayload(): RequestUpdateStylistPayload {
    return {
      stylist: this.selectedStylist && this.selectedStylist.id ? this.selectedStylist.id : '',
      note: this.note,
    };
  }

  protected get isPlanner(): boolean {
    if (!this.$store || !this.$store.state || !this.$store.state.Auth) {
      return false;
    }

    return this.$store.state.Auth.isPlanner();
  }

  protected deleteRequest() {
    this.$store.dispatch('openDialog', this.deleteDialogOptions);
  }

  get deleteDialogOptions(): Options {
    return {
      title: this.$t('dialogOptions.confirmation').toString(),
      text: 'Weet je zeker dat je deze aanvraag wilt verwijderen?',
      type: 'warning',
      buttons: {
        confirm: {
          text: this.$t('dialogOptions.button.delete').toString(),
          action: () => {
            if (!this.request) {
              return;
            }

            new BoxRequest()
              .deleteRequest(this.request.id as string)
              .then(() => {
                this.$emit('refresh');
              })
              .catch((error: AxiosError) => {
                ErrorHandler.network(error);
              });
          },
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString(),
        },
      },
    };
  }

  get statusOption(): Options {
    return {
      title: `Invoice ${this.status}`,
      text: `Invoice is ${this.status}`,
      type: 'success',
      buttons: {
        confirm: {
          text: 'Oke',
        },
      },
    };
  }
}

type ListUserFilters = {
  role?: string;
  search?: string;
};

type RequestUpdateStylistPayload = {
  stylist?: string;
  note?: string;
};
